import React, { useCallback, useEffect, useRef, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setIntroTransitionActive,
  setUserInterfaceDisabled,
} from "../../../../../../State/actions";
import { attemptFullscreenToggle } from "../../../../../../utilities";
import {
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectIsHoldAndWinMidFeatureReload,
  selectIsHoldAndWinIntroReload,
  selectCurrentRound,
} from "../../../../../../State/selectors";
import { SOUND_NAME } from "../../../../../../sounds";
import { useDimensions } from "../../../../../../Contexts/DimensionsContext";
import {
  useInitialIdleAnimations,
  useHoldAndWinIdleAnimations,
  useTriggerCoinWinAnimations,
} from "../../../../../../Hooks/useLandingAnimations";
import * as PIXI from "pixi.js";
import { soundManager } from "../../../../../../SoundManager";
import IntroPopupController from "./IntroPopupController";
import { safeInit } from "../../../../../../utilities";
// import ButtonSound from "../../../../secondary/Buttons/ButtonSound";
import { useToolbarDimensionsV2 } from "../../../../../../Hooks/useToolbarDimensionsV2";
import { FEATURES_CONFIG } from "../../../../../../featuresConfig";

/**
 * IntroPopup component - Displays intro screen with spine animation
 * Text is injected into spine slots (txt_slot_maxwin, txt_slot_click_to, txt_slot_feature_1, txt_slot_feature_2)
 */
const PixiIntroPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const introPopupActive = useSelector(selectIntroPopupActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const { dimensions, uiSymbolSize, uiSymbolScale, device, orientation } = useDimensions();
  // eslint-disable-next-line no-unused-vars
  const toolbar = useToolbarDimensionsV2();
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { triggerHoldAndWinIdleAnimations } = useHoldAndWinIdleAnimations();
  const { triggerCoinWinAnimations } = useTriggerCoinWinAnimations();
  const isHoldAndWinMidFeatureReload = useSelector(
    selectIsHoldAndWinMidFeatureReload,
  );
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const currentRound = useSelector(selectCurrentRound);

  // Container ref for spine controller
  const containerRef = useRef(null);
  // Spine controller
  const introControllerRef = useRef(null);

  // Debounce timeout for click protection (prevents rapid click buildup)
  const debounceTimeoutRef = useRef(null);
  // Pending RAF IDs for Hold and Win animation delay - cancelled when popup closes
  const pendingRafIdsRef = useRef([]);

  // Handle popup cleanup when closed
  useEffect(() => {
    if (!introPopupActive) {
      // Cancel any pending RAFs from Hold and Win animation delay
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];

      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
        introControllerRef.current = null;
      }
    }
  }, [introPopupActive]);

  // Initialize spine controller when popup becomes active
  useEffect(() => {
    if (introPopupActive && containerRef.current && !introControllerRef.current) {
      safeInit(() => {
        introControllerRef.current = new IntroPopupController(
          containerRef.current,
          dimensions,
          uiSymbolSize,
          { device, orientation, uiSymbolScale },
          t,
        );
      });
    }
    dispatch(setUserInterfaceDisabled(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive]);

  // Handle dimension changes and device/orientation changes
  useEffect(() => {
    if (introPopupActive && introControllerRef.current) {
      introControllerRef.current.updateDimensions(dimensions, uiSymbolSize, {
        device,
        orientation,
        uiSymbolScale,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive, dimensions.width, dimensions.height, uiSymbolSize, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending RAFs
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];

      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
        introControllerRef.current = null;
      }

      // Cleanup debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(
    (event) => {
      // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
      // This ensures only ONE click is processed, all others are ignored
      if (debounceTimeoutRef.current) {
        return;
      }

      // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks
      // This timeout will be the ONLY timeout created, no matter how many clicks
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
      }, 300);

      event.stopPropagation();
      soundManager.play(SOUND_NAME.button_click);

      // Attempt to toggle fullscreen if conditions are met
      attemptFullscreenToggle(dispatch, device);

      // Check if current round has wins - if yes, skip idle animations and let win controller handle it
      const hasWins = currentRound?.win > 0;

      // Trigger initial idle animations immediately when user clicks
      // Game is already rendered underneath at alpha 0.001
      if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && isHoldAndWinMidFeatureReload) {
        // For mid-feature reload ONLY, trigger both base game and Hold and Win idle animations
        triggerInitialIdleAnimations();
        // Wait 2 frames for Hold and Win components to be fully ready (track for cleanup)
        const id1 = requestAnimationFrame(() => {
          const id2 = requestAnimationFrame(() => {
            pendingRafIdsRef.current = pendingRafIdsRef.current.filter(
              (id) => id !== id1 && id !== id2,
            );
            triggerHoldAndWinIdleAnimations();
          });
          pendingRafIdsRef.current.push(id2);
        });
        pendingRafIdsRef.current.push(id1);
      } else if (
        FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
        isHoldAndWinIntroReload
      ) {
        // For intro reload (trigger round): Show WIN animations for coins on BASE GAME overlay
        // Coins are still in base game at this point, not in Hold and Win yet
        triggerInitialIdleAnimations();
        triggerCoinWinAnimations();
      } else if (hasWins) {
        // Winning round - skip idle animations, let win controller handle win animations
      } else if (FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED) {
        // Non-winning round - trigger idle animations (only if enabled)
        triggerInitialIdleAnimations();
      }

      dispatch(setIntroTransitionActive(true));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      device,
      triggerInitialIdleAnimations,
      triggerHoldAndWinIdleAnimations,
      triggerCoinWinAnimations,
      isHoldAndWinIntroReload,
      currentRound,
    ],
  );

  // Keyboard event listener for space bar
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when intro popup is active and UI is not disabled
      if (!introPopupActive || userInterfaceDisabled) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Execute the click handler
      handleClick(event);
    };

    // Use capture phase to ensure we get the event first, only when popup is active
    if (introPopupActive && !userInterfaceDisabled) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [introPopupActive, userInterfaceDisabled, handleClick]);

  // Sprite dimensions for click detection (full-screen invisible hit area)
  const spriteDimensions = useMemo(
    () => ({
      width: dimensions.width,
      height: dimensions.height,
    }),
    [dimensions.width, dimensions.height],
  );

  // Don't render anything if popup is not active
  if (!introPopupActive) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="intro"
      x={0}
      y={0}
      zIndex={100}
    >
      {/* Full-screen invisible sprite for click handling - transparent, clickable */}
      <Sprite
        texture={PIXI.Texture.EMPTY}
        x={0}
        y={0}
        width={spriteDimensions.width}
        height={spriteDimensions.height}
        zIndex={1}
        eventMode="static"
        cursor="pointer"
        pointertap={handleClick}
      />

      {/* Sound button - disabled for backport UI (has its own sound button) */}
      {/* <ButtonSound toolbar={toolbar} zIndex={10} /> */}
    </Container>
  );
};

export default PixiIntroPopup;
